import { z } from 'zod'
import type { ModeloAnalisado, PrecosPorCategoria, ContagemDeModelos } from '../api-oficial'
import { textoObrigatorio } from './comum'

/**
 * API oficial do WhatsApp (ADR 0033): a aba dentro de Números, só do
 * proprietário. Credenciais da Meta, retrato da conta, modelos e custo.
 */

export const PROVEDORES = ['evolution', 'oficial'] as const
export type Provedor = (typeof PROVEDORES)[number]

const idDaMeta = (campo: string) =>
  z
    .string()
    .trim()
    .regex(/^\d{5,40}$/, `o ${campo} tem só números (copie do painel da Meta)`)

/**
 * As credenciais. Token e chave secreta são opcionais na edição: em branco,
 * fica o que já estava salvo, que a tela nunca recebe inteiro de volta.
 */
export const salvarContaOficialSchema = z.object({
  phoneNumberId: idDaMeta('Phone Number ID'),
  wabaId: idDaMeta('WhatsApp Business Account ID'),
  token: z.string().trim().min(20, 'o token de acesso parece incompleto').max(2000).optional(),
  segredoApp: z
    .string()
    .trim()
    .min(16, 'a chave secreta do app parece incompleta')
    .max(200)
    .optional(),
  versaoApi: z
    .string()
    .trim()
    .regex(/^v\d{1,3}\.\d$/, 'a versão da API é algo como v23.0')
    .default('v23.0'),
})
export type SalvarContaOficialEntrada = z.infer<typeof salvarContaOficialSchema>

/** Número que já nasce na API oficial, sem passar pelo QR code. */
export const novoNumeroOficialSchema = salvarContaOficialSchema.extend({
  nome: textoObrigatorio('nome do número', 80),
  numero: z.string().trim().min(10, 'informe o número com DDD').max(20),
  token: z.string().trim().min(20, 'o token de acesso parece incompleto').max(2000),
  segredoApp: z.string().trim().min(16, 'a chave secreta do app parece incompleta').max(200),
})
export type NovoNumeroOficialEntrada = z.infer<typeof novoNumeroOficialSchema>

export const precosOficiaisSchema = z.object({
  marketing: z.number().min(0).max(100),
  utilidade: z.number().min(0).max(100),
  autenticacao: z.number().min(0).max(100),
})
export type PrecosOficiaisEntrada = z.infer<typeof precosOficiaisSchema>

export const trocarProvedorSchema = z.object({ provedor: z.enum(PROVEDORES) })

/** Mandar um modelo aprovado na conversa. `valores` usa `chaveDaVariavel`. */
export const enviarModeloSchema = z.object({
  modeloId: z.string().uuid(),
  valores: z.record(z.string(), z.string().max(1024)).default({}),
})
export type EnviarModeloEntrada = z.infer<typeof enviarModeloSchema>

export interface ContaOficialDto {
  conexaoId: string
  phoneNumberId: string
  wabaId: string
  versaoApi: string
  /** Só os quatro últimos caracteres. */
  tokenFinal: string
  segredoFinal: string
  /** O que se cola no campo "Verify token" do webhook na Meta. */
  tokenVerificacao: string
  /** O que se cola no campo "Callback URL" do webhook na Meta. */
  urlDoWebhook: string
  numeroExibido: string | null
  nomeVerificado: string | null
  qualidade: string | null
  limiteDiario: string | null
  conferidoEm: string | null
  erroDaConferencia: string | null
  precos: PrecosPorCategoria
  modelosSincronizadosEm: string | null
}

export interface ModeloOficialDto {
  id: string
  nome: string
  idioma: string
  categoria: string
  situacao: string
  motivoRecusa: string | null
  analise: ModeloAnalisado
  atualizadoEm: string
}

export interface NumeroNaApiOficialDto {
  conexaoId: string
  nome: string
  numero: string
  provedor: Provedor
  status: string
  conta: ContaOficialDto | null
  modelos: ModeloOficialDto[]
  custoDoMes: {
    /** "2026-09". */
    mes: string
    contagem: ContagemDeModelos
    porCategoria: ContagemDeModelos
    total: number
  }
}

/** A janela de 24 horas de uma conversa na API oficial. */
export interface JanelaDto {
  aberta: boolean
  /** Quando fecha; `null` se o cliente nunca escreveu por este número. */
  fechaEm: string | null
}
